import { useState, useEffect, useCallback, useRef } from 'react';
import { toast } from 'sonner';

const API = '/api';
const POLL_INTERVAL = 30000; // 30 seconds

export function useReminderPolling(user) {
    const [dueReminders, setDueReminders] = useState([]);
    const shownRef = useRef(new Set());
    const intervalRef = useRef(null);

    const checkReminders = useCallback(async () => {
        if (!user) return;
        try {
            const res = await fetch(`${API}/reminders/due`, { credentials: 'include' });
            if (!res.ok) return;
            const reminders = await res.json();

            reminders.forEach(r => {
                if (!shownRef.current.has(r.reminder_id)) {
                    shownRef.current.add(r.reminder_id);
                    setDueReminders(prev => [...prev, r]);
                }
            });
        } catch {
            // Silent fail - polling will retry
        }
    }, [user]);

    useEffect(() => {
        if (!user) return;
        // Check immediately on mount
        checkReminders();
        // Then poll every 30 seconds
        intervalRef.current = setInterval(checkReminders, POLL_INTERVAL);
        return () => {
            if (intervalRef.current) clearInterval(intervalRef.current);
        };
    }, [user, checkReminders]);

    const dismissReminder = useCallback(async (reminderId) => {
        setDueReminders(prev => prev.filter(r => r.reminder_id !== reminderId));
        // Mark as seen on server so it doesn't show again
        try {
            await fetch(`${API}/reminders/${reminderId}/dismiss`, {
                method: 'POST',
                credentials: 'include',
            });
        } catch {
            // Non-fatal
        }
    }, []);

    const snoozeReminder = useCallback(async (reminderId, preset) => {
        try {
            const res = await fetch(`${API}/reminders/${reminderId}/snooze`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({ preset }),
            });
            if (res.ok) {
                setDueReminders(prev => prev.filter(r => r.reminder_id !== reminderId));
                toast.success('Reminder snoozed');
            }
        } catch {
            toast.error('Failed to snooze');
        }
    }, []);

    return { dueReminders, dismissReminder, snoozeReminder };
}
